import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, OnInit, OnDestroy, Optional, Self, HostBinding } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { TimeService } from '../../../services/time.service';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
	selector: 'bb-timecode-editor',
	templateUrl: './bb-timecode-editor.component.html',
	styleUrls: ['./bb-timecode-editor.component.scss'],
	providers: [{
		provide: MatFormFieldControl,
		useExisting: forwardRef(() => BbTimecodeEditorComponent)
	}]
})

export class BbTimecodeEditorComponent implements MatFormFieldControl<string>, ControlValueAccessor, OnInit, OnDestroy {
	@Input() forceHourDisplay = false;
	@Input() hideSeconds = false;
	@Input() hideFrames = false;
	@Input() useMilliseconds = false;
	@Input() min: number;
	@Input() max: number;
	@Input() fps = 25;
	@Input() inMatFormField = false;
	@Output() input: EventEmitter<number> = new EventEmitter<number>();
	@Output() onFocus: EventEmitter<Event> = new EventEmitter<Event>();
	@Output() onBlur: EventEmitter<Event> = new EventEmitter<Event>();
	@Output() change: EventEmitter<number> = new EventEmitter<number>();
	@ViewChild('timeStringInput') timeStringInput: ElementRef;
	modelValue: number;
	timeString: string;

	// for MatFormFieldControl
	controlType = 'bb-timecode-editor';
	touched = false;
	stateChanges = new Subject<void>();
	private _disabled = false;
	private _required = false;
	private _focused = false;

	// for MatFormFieldControl
	static nextId = 0;
	@HostBinding() id = `bb-timecode-editor-${BbTimecodeEditorComponent.nextId++}`;
	@HostBinding('class.floating') get shouldLabelFloat() {
		return this.focused || !this.empty;
	}
	@Input('aria-describedby') userAriaDescribedBy: string;
	@Input() get required() {
		return this._required;
	}
	set required(req) {
		this._required = coerceBooleanProperty(req);
		this.stateChanges.next();
	}
	@Input() get disabled(): boolean {
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this.stateChanges.next();
	}
	@Input() placeholder: string;

	constructor(
		private timeService: TimeService,
		@Optional() @Self() public ngControl: NgControl,
	) {
		if (this.ngControl !== null) {
			this.ngControl.valueAccessor = this;
		}
	}

	ngOnInit() { }

	ngOnDestroy() {
		this.stateChanges.complete();
	}

	// for MatFormFieldControl
	get value() {
		return this.timeString;
	}
	get empty() {
		return this.timeString === '' || this.timeString === null;
	}
	get focused() {
		return this._focused;
	}
	get errorState(): boolean {
		return !this.modelValue && this.required;
	}
	setDescribedByIds(ids: string[]) {
		// maybe later, required method.
	}

	onContainerClick(event: MouseEvent) {
		this.timeStringInput.nativeElement.focus();
	}

	// end MatFormFieldControl things

	onClick(ev: MouseEvent) {

	}

	onInput(ev: Event) {
		this.input.emit(this.timeService.timeStringToSeconds(this.timeString + (this.hideSeconds ? ':00' : ''), this.fps, this.useMilliseconds));
	}

	onChange(ev: Event) {
		let seconds = this.timeService.timeStringToSeconds(this.timeString + (this.hideSeconds ? ':00' : ''), this.fps, this.useMilliseconds);
		if (isNaN(seconds)) {
			// invalid format, revert to old value
			this.timeString = this.timeService.secondsToTimeString(this.modelValue, this.forceHourDisplay, this.fps, this.hideSeconds, this.hideFrames, this.useMilliseconds);
		} else {
			if (this.min !== undefined && seconds < this.min) {
				seconds = this.min;
			} else if (this.max !== undefined && seconds > this.max) {
				seconds = this.max;
			}
			this.timeString = this.timeService.secondsToTimeString(seconds, this.forceHourDisplay, this.fps, this.hideSeconds, this.hideFrames, this.useMilliseconds);
			this.propagateChange(seconds);
			this.change.emit(seconds);
			// for MatFormFieldControl
			this.stateChanges.next();
		}
	}

	onInputFocus(ev: Event) {
		this.onFocus.emit(ev);
		this._focused = true;
	}

	onInputBlur(ev: Event) {
		this._focused = false;
		this.onBlur.emit(ev);
	}

	// to act as an input
	focus() {
		if (this.timeStringInput && this.timeStringInput.nativeElement) {
			this.timeStringInput.nativeElement.focus();
		}
	}
	blur() {
		if (this.timeStringInput && this.timeStringInput.nativeElement) {
			this.timeStringInput.nativeElement.blur();
		}
	}
	select() {
		if (this.timeStringInput && this.timeStringInput.nativeElement) {
			this.timeStringInput.nativeElement.select();
		}
	}

	// ControlValueAccessor fields:
	writeValue(value: number) {
		if (!value) {
			value = 0;
		}
		this.modelValue = Number(value);
		this.timeString = this.timeService.secondsToTimeString(this.modelValue, this.forceHourDisplay, this.fps, this.hideSeconds, this.hideFrames, this.useMilliseconds);
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}
}
